import { createRoot } from "react-dom/client";
import { lazy, Suspense, type ReactNode } from "react";

import { SignupModalProvider } from "@/components/popups/SignupModalContext";
import { LazySignupModal } from "@/components/popups/LazySignupModal";
import { PrivacyNotice } from "@/components/popups/PrivacyNotice";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { ScrollProgress } from "@/components/layout/ScrollProgress";
import { DeferredNanoNetworkBackground } from "@/components/nano-background/DeferredNanoNetworkBackground";
import { WhatsAppFloat } from "@/components/layout/WhatsAppFloat";

import { Hero } from "@/components/sections/Hero";
import { MarqueeBand } from "@/components/sections/MarqueeBand";
import { BuiltForScale } from "@/components/sections/BuiltForScale";
import { HomeDeferredSections } from "@/wp/pages/HomeDeferredSections";

import { PrivacidadeContent } from "@/wp/pages/PrivacidadeContent";
import { NotFoundContent } from "@/wp/pages/NotFoundContent";
import { LocaleProvider, type Locale } from "@/i18n/locale";

// Página de pré-venda separada da home, com sua própria seção pesada
// (WifiParticleField, timeline animada) — carregada só quando alguém
// realmente visita /sat-labs-cast, em vez de inflar o app.js que toda
// visita à home baixa (essa era a maior fatia de "JavaScript não usado"
// apontada pelo PageSpeed na home).
const CastLanding = lazy(() =>
  import("@/app/sat-labs-cast/CastLanding").then((m) => ({ default: m.CastLanding }))
);

// Ponto de entrada do bundle standalone usado dentro do tema WordPress. Monta
// a MESMA árvore de componentes React do site Next.js (Header, seções,
// popups) num único <div id="satlabs-root">, escolhendo o conteúdo da página
// pelo atributo data-page que o PHP imprime em cada template.

function AppShell({ children, locale }: { children: ReactNode; locale: Locale }) {
  return (
    <LocaleProvider locale={locale}>
    <SignupModalProvider>
      <ScrollProgress />
      <DeferredNanoNetworkBackground />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-accent-lime focus:px-4 focus:py-2 focus:text-ink-black"
      >
        Pular para o conteúdo
      </a>
      <Header />
      {/* Header agora é fixed — ver mesmo pt-18/-mt-18 em layout.tsx e Hero.tsx. */}
      <main id="main-content" className="flex-1 pt-18">
        <Suspense fallback={null}>{children}</Suspense>
      </main>
      <Footer />
      <LazySignupModal />
      <PrivacyNotice />
      <WhatsAppFloat />
    </SignupModalProvider>
    </LocaleProvider>
  );
}

function HomeContent() {
  return (
    <>
      <Hero />
      <MarqueeBand items={["Menos falhas", "Mais controle", "Operação integrada", "Estrutura para crescer"]} />
      <BuiltForScale />
      <HomeDeferredSections />
    </>
  );
}

const PAGES: Record<string, () => ReactNode> = {
  home: () => <HomeContent />,
  "sat-labs-cast": () => <CastLanding />,
  privacidade: () => <PrivacidadeContent />,
  "404": () => <NotFoundContent />,
};

const rootEl = document.getElementById("satlabs-root");
if (rootEl) {
  const page = rootEl.dataset.page ?? "home";
  const locale: Locale = rootEl.dataset.locale === "en" ? "en" : "pt";
  const renderPage = PAGES[page] ?? PAGES.home;
  createRoot(rootEl).render(<AppShell locale={locale}>{renderPage()}</AppShell>);
}
